// @ts-nocheck

import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as AuthorIcon } from '../../../../assets/svg/user-grid.svg';
import { Target } from '../../../../client';
import Formatter from '../../../../i18n/formatter';
import { __ } from '../../../../i18n/l10n';
import type { GridMatchLength } from '../../types';
import CurrencyExposant from '../CurrencyExposant';
import Avatar from '../history/Avatar';

import Badge from './Badge';
import Date from './Date';
import ProgressBar from './ProgressBar';

const Wrap = styled.div`
  position: relative;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.33);
  background: #fff;
  border-radius: 0.2em;
  padding: 0.4em 0.6em;
  display: flex;
  justify-content: space-around;
  align-items: center;
  color: ${({ theme }) => theme.grids.colors.black};
`;

const WrapNickname = styled.p`
  font-size: 1em;
  margin: 0;
  ${!Target.__MOBILE__
    ? css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      `
    : undefined}
`;

const Nickname = styled.span`
  margin-right: 0.2em;
`;

const VerticalBar = styled.div`
  width: 1px;
  height: ${({ $isBasket }) => ($isBasket ? 2 : 4)}em;
  background: ${({ theme }) => theme.grids.colors.black};
  margin: 0 0.6em;
`;

const HorizontalBar = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.grids.colors.lightgrey};
`;

const AlignLeft = styled.div`
  text-align: left;
  flex-grow: 1;
`;

const StyledAvatar = styled(Avatar)`
  margin: 0 1em 0 0;
`;

const Left = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;

  ${!Target.__MOBILE__
    ? css`
        min-height: 70px;
      `
    : undefined}
`;

const PoolInfos = styled.p`
  margin: 0.4em auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;
`;

const Red = styled.span`
  color: ${({ theme }) => theme.grids.colors.redlight};
  display: inline-block;
  margin-right: 0.2em;
`;

const PlayerWinnings = styled(CurrencyExposant)`
  display: inline-block;
`;

const WrapContributorsCount = styled.div`
  text-align: left;
  padding: 0;
  min-width: 30%;
`;

const ContributorsCount = styled.div`
  font-size: 1em;
  margin: 0 0 0.2em 0;
  display: flex;
  justify-content: ${({ $isBasket }) => ($isBasket ? 'center' : 'flex-start')};
  align-items: center;
  line-height: 1;
`;

const StyledContributorIcon = styled(AuthorIcon)`
  width: 1.2em;
  height: 1.2em;
  color: ${({ theme }) => theme.grids.colors.redlight};
  margin: 0 0.3em 0 0;
`;

type GridsAuthorProps = {
  className?: string;
  nickname: string | null;
  avatarPath: string | null;
  winnings: number | null;
  gridAmount: number | null;
  fundingAmount: number | null;
  contributorsCount: number | null;
  gridDate: number | null;
  gridLength: GridMatchLength | null;
  isBasket?: boolean;
  badges?: Array<number>;
};

const GridsAuthor = ({
  className,
  nickname,
  avatarPath,
  winnings,
  gridAmount,
  fundingAmount,
  contributorsCount,
  gridDate,
  gridLength,
  isBasket = false,
  badges,
}: GridsAuthorProps) => {
  const renderContributorsBloc = useCallback(() => {
    if (contributorsCount) {
      return (
        <ContributorsCount $isBasket={isBasket}>
          <StyledContributorIcon />
          <span>{contributorsCount}</span>
        </ContributorsCount>
      );
    }
    return null;
  }, [contributorsCount, isBasket]);

  const renderProgressBloc = useCallback(() => {
    if (gridAmount && fundingAmount) {
      return <ProgressBar max={gridAmount} value={fundingAmount} />;
    }
    return null;
  }, [fundingAmount, gridAmount]);

  return (
    <div className={className}>
      <Wrap>
        <Left>
          <StyledAvatar image={avatarPath} />
          <AlignLeft>
            <WrapNickname>
              <Nickname>{nickname}</Nickname>{' '}
              {!isBasket && badges && badges.map((badge) => <Badge key={badge} type={badge} />)}
            </WrapNickname>
            {gridLength && winnings && (
              <>
                <HorizontalBar />
                <PoolInfos>
                  {gridLength && <Red>{__('Grid %1$s winnings', gridLength)}</Red>}
                  {winnings && <PlayerWinnings amount={winnings} />}
                </PoolInfos>
              </>
            )}
          </AlignLeft>
        </Left>
        <VerticalBar $isBasket={isBasket} />
        {isBasket ? (
          <>
            {renderContributorsBloc()}
            <VerticalBar $isBasket={isBasket} />
            {renderProgressBloc()}
          </>
        ) : (
          <>
            <WrapContributorsCount>
              {renderContributorsBloc()}
              {renderProgressBloc()}
            </WrapContributorsCount>
          </>
        )}
      </Wrap>
      {gridDate && <Date text={Formatter.formatDate(gridDate, 'dateMedium')} />}
    </div>
  );
};

export default GridsAuthor;
